import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { SafeAvatarImage } from '@/components/ui/safe-avatar-image';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle, 
  Users, 
  Video, 
  Heart,
  ExternalLink,
  UserPlus,
} from 'lucide-react';
import { CreatorGroup } from '@/hooks/useGroupedCreators';
import { getProxiedImageUrl } from '@/utils/imageProxy';
import { TikTokIcon, InstagramIcon, YouTubeIcon, getPlatformIcon } from '@/components/marketplace/PlatformIcons';

interface GroupedCreatorDetailModalProps {
  creatorGroup: CreatorGroup | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteToDeal?: (creatorGroup: CreatorGroup) => void;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
};

const getPlatformColor = (platform: string): string => {
  switch (platform) {
    case 'tiktok':
      return 'bg-[#00f2ea]/10 text-[#00f2ea] border-[#00f2ea]/30';
    case 'instagram':
      return 'bg-gradient-to-r from-[#833ab4]/10 via-[#fd1d1d]/10 to-[#fcb045]/10 text-[#fd1d1d] border-[#fd1d1d]/30';
    case 'youtube':
      return 'bg-[#ff0000]/10 text-[#ff0000] border-[#ff0000]/30';
    default:
      return 'bg-muted text-muted-foreground';
  }
};

const getPlatformUrl = (platform: string, username: string): string => {
  switch (platform) {
    case 'tiktok':
      return `https://www.tiktok.com/@${username}`;
    case 'instagram':
      return `https://www.instagram.com/${username}`;
    case 'youtube':
      return `https://www.youtube.com/channel/${username}`;
    default:
      return '#';
  }
};

export const GroupedCreatorDetailModal: React.FC<GroupedCreatorDetailModalProps> = ({
  creatorGroup,
  open,
  onOpenChange,
  onInviteToDeal,
}) => {
  if (!creatorGroup) return null;

  const isMultiPlatform = creatorGroup.platforms.length > 1;
  const primaryCreator = creatorGroup.creators[0];

  // Calculate combined stats with null safety
  const totalFollowers = creatorGroup.creators.reduce((sum, c) => sum + (c.follower_count ?? 0), 0);
  const totalFollowing = creatorGroup.creators.reduce((sum, c) => sum + (c.following_count ?? 0), 0);
  const totalVideos = creatorGroup.creators.reduce((sum, c) => sum + (c.video_count ?? 0), 0);
  const totalLikes = creatorGroup.creators.reduce((sum, c) => sum + (c.like_count ?? 0), 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 overflow-hidden">
        <ScrollArea className="max-h-[90vh]">
          {/* Header with gradient based on platforms */}
          <div className="relative">
            <div className={`h-32 ${
              creatorGroup.platforms.includes('tiktok') && creatorGroup.platforms.includes('youtube') 
                ? 'bg-gradient-to-r from-[#00f2ea]/30 via-[#fd1d1d]/30 to-[#ff0000]/30'
                : creatorGroup.platforms.includes('tiktok') 
                  ? 'bg-gradient-to-r from-[#00f2ea]/30 to-[#ff0050]/30'
                  : creatorGroup.platforms.includes('instagram')
                    ? 'bg-gradient-to-r from-[#833ab4]/30 via-[#fd1d1d]/30 to-[#fcb045]/30'
                    : 'bg-gradient-to-r from-[#ff0000]/30 to-[#ff0000]/20'
            }`} />
            <div className="absolute -bottom-12 left-6">
              <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
                <SafeAvatarImage src={getProxiedImageUrl(creatorGroup.avatar_url || '')} className="object-cover" />
                <AvatarFallback className="text-2xl">
                  {(creatorGroup.display_name || creatorGroup.primary_name || 'U')[0]}
                </AvatarFallback>
              </Avatar>
            </div>
            {/* Platform badges */}
            <div className="absolute top-4 right-4 flex gap-2">
              {creatorGroup.platforms.map(platform => (
                <div 
                  key={platform}
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full border ${getPlatformColor(platform)}`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">{getPlatformIcon(platform)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="pt-14 px-6 pb-6 space-y-6">
            <DialogHeader>
              <div className="flex items-center gap-2">
                <DialogTitle className="text-2xl font-bold">
                  {creatorGroup.display_name || creatorGroup.primary_name}
                </DialogTitle>
                {creatorGroup.is_verified && (
                  <CheckCircle className="h-5 w-5 text-primary fill-primary/20" />
                )}
              </div>
              {isMultiPlatform && (
                <p className="text-muted-foreground">
                  {creatorGroup.platforms.length} connected platforms
                </p>
              )}
            </DialogHeader>

            {/* Combined Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(totalFollowers)}</p>
                <p className="text-xs text-muted-foreground">Total Followers</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Users className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(totalFollowing)}</p>
                <p className="text-xs text-muted-foreground">Following</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Video className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(totalVideos)}</p>
                <p className="text-xs text-muted-foreground">Total Videos</p>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 text-center">
                <Heart className="h-5 w-5 mx-auto mb-2 text-muted-foreground" />
                <p className="text-2xl font-bold">{formatNumber(totalLikes)}</p>
                <p className="text-xs text-muted-foreground">Total Likes</p>
              </div>
            </div>

            {/* Platform Breakdown */}
            {isMultiPlatform && (
              <>
                <Separator />
                <div className="space-y-3">
                  <h4 className="text-sm font-medium">Platform Breakdown</h4>
                  <div className="grid gap-3">
                    {creatorGroup.creators.map(creator => (
                      <Card key={creator.id} className={`border-l-4 ${
                        creator.platform === 'tiktok' ? 'border-l-[#00f2ea]' :
                        creator.platform === 'instagram' ? 'border-l-[#fd1d1d]' :
                        'border-l-[#ff0000]'
                      }`}>
                        <CardContent className="p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-full ${getPlatformColor(creator.platform)}`}>
                                <span className="w-5 h-5 flex items-center justify-center">{getPlatformIcon(creator.platform)}</span>
                              </div>
                              <div>
                                <p className="font-medium capitalize">{creator.platform}</p>
                                <p className="text-sm text-muted-foreground">@{creator.username}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold">{formatNumber(creator.follower_count ?? 0)}</p>
                              <p className="text-xs text-muted-foreground">followers</p>
                            </div>
                          </div>
                          <div className="mt-3 flex gap-4 text-sm text-muted-foreground">
                            <span>{formatNumber(creator.video_count ?? 0)} videos</span>
                            <span>{formatNumber(creator.like_count ?? 0)} likes</span>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-3 w-full"
                            asChild
                          >
                            <a 
                              href={creator.profile_url || getPlatformUrl(creator.platform, creator.username)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <ExternalLink className="h-3 w-3 mr-2" />
                              View {creator.platform} Profile
                            </a>
                          </Button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Bio */}
            {(creatorGroup.bio || primaryCreator?.bio) && (
              <>
                <Separator />
                <div className="space-y-2">
                  <h4 className="text-sm font-medium">About</h4>
                  <p className="text-sm text-muted-foreground">
                    {creatorGroup.bio || primaryCreator?.bio}
                  </p>
                </div>
              </>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              {!isMultiPlatform && primaryCreator && (
                <Button className="flex-1" asChild>
                  <a 
                    href={primaryCreator.profile_url || getPlatformUrl(primaryCreator.platform, primaryCreator.username)} 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    View Profile
                  </a>
                </Button>
              )}
              <Button 
                variant={isMultiPlatform ? "default" : "outline"}
                className="flex-1"
                onClick={() => {
                  if (creatorGroup && onInviteToDeal) {
                    onInviteToDeal(creatorGroup);
                    onOpenChange(false);
                  }
                }}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Invite to Deal
              </Button>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
